import { Box, CircularProgress, Stack } from '@mui/material'
import { ArrowRightIcon } from '@phosphor-icons/react'
import { ReactNode } from 'react'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { Typography } from '../../text/Typography/Typography'

type IllustrationCardProps = {
    image: ReactNode
    title: string
    text?: string
    onClick: () => void
    variant?: 'vertical' | 'horizontal'
    arrowLabel?: ReactNode
    contentChip?: ReactNode
    hideArrow?: boolean
    disabled?: boolean
    isPending?: boolean
    hoverColor?: string
    icon?: ReactNode
}

export const IllustrationCard = ({
    image,
    text,
    title,
    onClick,
    variant = 'vertical',
    arrowLabel,
    contentChip,
    hideArrow,
    hoverColor,
    disabled,
    icon,
    isPending,
}: IllustrationCardProps) => {
    const isHorizontal = variant === 'horizontal'
    const backgroundColor = disabled ? tokens.ColorSurfaceDisabled : tokens.ColorSurfaceDefault

    return (
        <Stack
            direction={isHorizontal ? 'row' : 'column'}
            padding={tokens.SizingSpacing150}
            width={isHorizontal ? '100%' : '200px'}
            minWidth={isHorizontal ? '386px' : undefined}
            maxWidth="440px"
            height={isHorizontal ? '159px' : '278px'}
            gap={isHorizontal ? tokens.SizingSpacing150 : undefined}
            sx={{
                border: `1px solid ${tokens.ColorBorderDefault}`,
                borderRadius: tokens.SizingCornerM,
                justifyContent: 'space-between',
                backgroundColor,
                cursor: disabled || isPending ? 'default' : 'pointer',
                '&:hover': {
                    backgroundColor: disabled || isPending ? backgroundColor : hoverColor || tokens.ColorSurfaceHover,
                },
            }}
            onClick={disabled || isPending ? undefined : onClick}
        >
            {isHorizontal ? (
                <>
                    <Box height="135px" minWidth="175px">
                        {image}
                    </Box>
                    <Stack direction="column" justifyContent="space-between" width="100%" height="100%" minWidth={0}>
                        <IllustrationCardText title={title} text={text} disabled={disabled} />
                        <IllustrationCardAction
                            contentChip={contentChip}
                            isPending={isPending}
                            hideArrow={hideArrow}
                            arrowLabel={arrowLabel}
                            icon={icon}
                        />
                    </Stack>
                </>
            ) : (
                <>
                    <Stack direction="column" gap={tokens.SizingSpacing200}>
                        <Box height="135px" width="175px">
                            {image}
                        </Box>
                        <IllustrationCardText title={title} text={text} disabled={disabled} />
                    </Stack>
                    <IllustrationCardAction
                        contentChip={contentChip}
                        isPending={isPending}
                        hideArrow={hideArrow}
                        arrowLabel={arrowLabel}
                        icon={icon}
                    />
                </>
            )}
        </Stack>
    )
}

function IllustrationCardText({ title, text, disabled }: Pick<IllustrationCardProps, 'title' | 'text' | 'disabled'>) {
    return (
        <Stack direction="column" gap={tokens.SizingSpacing075} minWidth={0}>
            <Typography variant="headline3" color={disabled ? tokens.ColorTextDisabled : tokens.ColorTextBoldest}>
                {title}
            </Typography>
            {text && (
                <Typography variant="textS" color={disabled ? tokens.ColorTextDisabled : tokens.ColorTextSubtle}>
                    {text}
                </Typography>
            )}
        </Stack>
    )
}

function IllustrationCardAction({
    contentChip,
    isPending,
    hideArrow,
    arrowLabel,
    icon,
}: Pick<IllustrationCardProps, 'contentChip' | 'isPending' | 'hideArrow' | 'arrowLabel' | 'icon'>) {
    const showAction = isPending || !hideArrow

    if (!contentChip && !showAction) {
        return null
    }

    return (
        <Stack direction="row" width="100%" alignItems="center" justifyContent={contentChip ? 'space-between' : 'end'}>
            {contentChip}
            {showAction &&
                (isPending ? (
                    <CircularProgress
                        thickness={4}
                        size={tokens.SizingIconS}
                        sx={{ position: 'relative', left: 'initial', top: 'initial' }}
                    />
                ) : (
                    <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100}>
                        {arrowLabel && (
                            <Typography variant="link" color={tokens.ColorTextLinkDefault}>
                                {arrowLabel}
                            </Typography>
                        )}
                        {icon || <ArrowRightIcon size={tokens.SizingIconM} color={tokens.ColorIconLinkDefault} />}
                    </Stack>
                ))}
        </Stack>
    )
}
